import React, { useState } from "react";
import { Input, Segmented } from "antd";

/**
 * HtmlBodyEditor — an HTML email/message body field with a Plain | HTML toggle.
 *
 *  - "Plain" (default) is the editable raw-HTML source (antd Input.TextArea) —
 *    identical to the bare <textarea> these editors used before.
 *  - "HTML" renders the body so the author sees what the email actually looks
 *    like. It is a READ-ONLY preview; editing happens in Plain mode.
 *
 * Drop-in replacement for the CRM body <Input.TextArea>s (send-reminder, email
 * compose, template editor). `onChange` is called with the raw string (not an
 * event) so a call site can pass `onChange={setBody}` directly.
 *
 * `initialMode`: which view it opens in. "html" (the preview) suits a body that
 * arrives pre-filled, like a template; a blank new email or reply opens in
 * "plain", ready to type.
 *
 * The body is trusted, staff-authored HTML written in this same admin UI, so it
 * is rendered with dangerouslySetInnerHTML — the same pattern already used for
 * CRM message/preview bodies in OfficeDetailDrawer.
 */
export default function HtmlBodyEditor({
  value,
  onChange,
  rows = 8,
  placeholder = "Message (HTML allowed)…",
  style,
  initialMode = "html",
}) {
  const [mode, setMode] = useState(initialMode);
  const previewMinHeight = rows * 22;

  return (
    <div style={style}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
        <Segmented
          size="small"
          value={mode}
          onChange={setMode}
          options={[
            { label: "Plain", value: "plain" },
            { label: "HTML", value: "html" },
          ]}
        />
      </div>
      {mode === "plain" ? (
        <Input.TextArea
          rows={rows}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <div
          className="html-body-editor-preview"
          style={{
            minHeight: previewMinHeight,
            maxHeight: 420,
            overflowY: "auto",
            padding: 12,
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            background: "#fff",
          }}
          dangerouslySetInnerHTML={{
            __html:
              value && value.trim()
                ? value
                : '<span style="color:#999">Nothing to preview yet — switch to Plain to write the message.</span>',
          }}
        />
      )}
    </div>
  );
}
